import { Divider, Input, InputNumber, Row, Select } from "antd";
import { FormItem } from "common/components/form/FormItem";
import {
  CatalogCountries,
  CatalogIncoterms,
  ComplementModel,
  getCountries,
  getIncoterms,
} from "api/generated";
import ResponsiveCol from "common/components/form/ResponsiveCol";
import { ExtraInfoBtn } from "common/helpers/generalUtils";
import { useClient } from "hooks/useClient";
import { useEffect, useState } from "react";
import {
  filterCombo,
  importExportPlace,
  internationalCustomDeclaration,
  internationalImportExportPermission,
  validateCantidadMonetaria,
  validateValorNumerico,
} from "services/utils/patterns";

export function InternationalComplementForm() {
  const { client } = useClient();
  const [countries, setCountries] = useState<CatalogCountries[]>([]);
  const [incoterms, setIncoterms] = useState<CatalogIncoterms[]>([]);

  useEffect(() => {
    getCountries({ client }).then(({ data }) => {
      setCountries(data ?? []);
    });

    getIncoterms({ client }).then(({ data }) => {
      setIncoterms(data ?? []);
    });
  }, [client]);

  return (
    <div>
      <Divider
        orientation="left"
        style={{ fontSize: "15px", borderColor: "#49f5f5ff" }}
      >
        Complemento - Extranjero
      </Divider>

      <Row gutter={24}>
        <FormItem<ComplementModel>
          hidden
          label="international_id"
          name={["international", "id"]}
        />

        <ResponsiveCol size="lg">
          <FormItem<ComplementModel>
            label="Permiso de importación o exportación"
            name={["international", "permission"]}
            rules={[
              { required: true, message: "Este campo es obligatorio" },
              {
                pattern: internationalImportExportPermission,
                message:
                  "Formato de permiso de importación o exportación inválido",
              },
            ]}
          >
            <Input
              className="common-input"
              placeholder="PermisoImportacionOExportacion"
              addonAfter={ExtraInfoBtn("Permiso de importación o exportación", [
                "Debes registrar permiso de importación o exportación emitido por la Secretaría de Energía.",
                "Ejemplo: PermisoImportacionOExportacion = 1234C123456789",
              ])}
            />
          </FormItem>
        </ResponsiveCol>

        <ResponsiveCol size="lg">
          <FormItem<ComplementModel>
            label="Punto de internación o extracción"
            name={["international", "importExportPlace"]}
            rules={[
              { required: true, message: "Este campo es obligatorio" },
              {
                min: 2,
                message: "Introduce mínimo 2 caracteres.",
              },
              {
                max: 3,
                message: "No se puede exceder los 3 caracteres.",
              },
              {
                pattern: importExportPlace,
                message: "Clave de aduana inválida",
              },
            ]}
          >
            <Input
              className="common-input"
              placeholder="PuntoDeInternacionOExtraccion"
              addonAfter={ExtraInfoBtn("Punto de internación o extracció", [
                "Debes registrar la clave de la aduana o sección por donde entró o salió la mercancía a territorio nacional, conforme al Apéndice 1 del Anexo 22 de las Reglas Generales de Comercio Exterior.",
                "Ejemplo: PuntoDeInternacionOExtraccion = 280",
              ])}
            />
          </FormItem>
        </ResponsiveCol>

        <ResponsiveCol size="lg">
          <FormItem<ComplementModel>
            label="País de origen o destino"
            name={["international", "countryCode"]}
            rules={[{ required: true, message: "Este campo es obligatorio" }]}
          >
            <Select
              showSearch
              filterOption={filterCombo}
              placeholder="Selecciona País de origen o destino"
              suffixIcon={ExtraInfoBtn("País de origen o destino", [
                "Debes señalar la clave SAAI M3 del país, grupo de países o territorio de la Parte exportadora, que corresponda al origen o destino de las mercancías o donde se produjeron, conforme al Apéndice 4 del Anexo 22 de las Reglas Generales de Comercio Exterior.",
                "Ejemplo: PaisOrigenODestino= USA",
              ])}
            >
              {countries.map((item) => {
                return (
                  <Select.Option key={item.id} value={item.saaiM3Code}>
                    {`${item.saaiM3Code} - ${item.country}`}
                  </Select.Option>
                );
              })}
            </Select>
          </FormItem>
        </ResponsiveCol>

        <ResponsiveCol size="lg">
          <FormItem<ComplementModel>
            label="Medio de entrada o salida de la aduana"
            name={["international", "importExportMethod"]}
            rules={[{ required: true, message: "Este campo es obligatorio" }]}
          >
            <Select
              showSearch
              filterOption={filterCombo}
              placeholder="Selecciona el medio de entrada o salida de la aduana"
              suffixIcon={ExtraInfoBtn(
                "Medio de entrada o salida de la aduana",
                [
                  "Debes registrar la clave del medio de transporte en que se conduce la mercancía para su entrada o salida del territorio nacional, conforme al Apéndice 3 del Anexo 22 de las Reglas Generales de Comercio Exterior.",
                  "Ejemplo: MedioDeTransEntraOSaleAduana= 1",
                ]
              )}
            >
              <Select.Option key={1} value={"1"}>
                1 - Marítimo
              </Select.Option>
              <Select.Option key={2} value={"2"}>
                2 - Ferroviario de doble estiba
              </Select.Option>
              <Select.Option key={3} value={"3"}>
                3 - Carretero-Ferroviario
              </Select.Option>
              <Select.Option key={6} value={"6"}>
                6 - Ferroviario
              </Select.Option>
              <Select.Option key={7} value={"7"}>
                7 - Carretero
              </Select.Option>
              <Select.Option key={8} value={"8"}>
                8 - Tubería
              </Select.Option>
              <Select.Option key={11} value={"11"}>
                11 - Ductos
              </Select.Option>
            </Select>
          </FormItem>
        </ResponsiveCol>

        <ResponsiveCol>
          <FormItem<ComplementModel>
            label="Pedimento aduanal"
            name={["international", "customsDeclaration"]}
            rules={[
              { required: true, message: "Este campo es obligatorio" },
              {
                pattern: internationalCustomDeclaration,
                message: "Número de pedimento aduanal inválido",
              },
            ]}
          >
            <Input
              className="common-input"
              placeholder="PedimentoAduanal"
              addonAfter={ExtraInfoBtn("Pedimento aduanal", [
                "Debes registrar el número del pedimento de importación conforme al numeral 1 del Instructivo para el llenado del Pedimento del Anexo 22 de las Reglas Generales de Comercio Exterior.",
                "Ejemplo: PedimentoAduanal= 20 28 3454 0123456",
              ])}
            />
          </FormItem>
        </ResponsiveCol>

        <ResponsiveCol>
          <FormItem<ComplementModel>
            label="Precio de importación o exportación"
            name={["international", "importExportAmount"]}
            rules={[
              { required: true, message: "Este campo es obligatorio" },
              {
                validator: validateCantidadMonetaria,
              },
            ]}
          >
            <InputNumber
              prefix="$"
              suffix="mxn"
              precision={5}
              className="common-input"
              placeholder="PrecioDeImportacionOExportacion"
              style={{ width: "100%" }}
              addonAfter={ExtraInfoBtn("Precio de importación o exportación", [
                "Es la cantidad de pesos mexicanos mediante la cual un permisionario de distribución introduce a territorio nacional un petrolífero. Incluye impuestos, pero no la tarifa de transporte ni almacenamiento.",
                "Ejemplo: PrecioDeImportacionOExportacion = 1000432.340",
              ])}
            />
          </FormItem>
        </ResponsiveCol>

        <ResponsiveCol>
          <FormItem<ComplementModel>
            label="Volumen de importación o exportación"
            name={["international", "volume"]}
            rules={[
              { required: true, message: "Este campo es obligatorio" },
              {
                validator: validateValorNumerico,
              },
            ]}
          >
            <InputNumber
              suffix="Litros"
              precision={3}
              className="common-input"
              placeholder="VolumenDocumentado"
              style={{ width: "100%" }}
              addonAfter={ExtraInfoBtn("Volumen de importación o exportación", [
                "Debes registrar el volumen expresado en el pedimento aduanal conforme a la TIGIE.",
                "Debes registrar el valor adimensional del volumen, es decir, la cantidad del volumen.",
                "Ejemplo: VolumenDocumentado = 1500000.000",
              ])}
            />
          </FormItem>
        </ResponsiveCol>

        <ResponsiveCol size="xl">
          <FormItem<ComplementModel>
            label="Incoterms"
            name={["international", "incoterms"]}
            rules={[{ required: true, message: "Este campo es obligatorio" }]}
          >
            <Select
              showSearch
              filterOption={filterCombo}
              placeholder="Selecciona Incoterms"
              suffixIcon={ExtraInfoBtn("Incoterms", [
                "Debes registrar la forma de facturación de acuerdo con los INCOTERMS internacionales vigentes, conforme al Apéndice 14 del Anexo 22 de las Reglas Generales de Comercio Exterior.",
                "Ejemplo: Incoterms = DAT",
              ])}
            >
              {incoterms.map((item) => {
                return (
                  <Select.Option key={item.id} value={item.code}>
                    {`${item.code} - ${item.description}`}
                  </Select.Option>
                );
              })}
            </Select>
          </FormItem>
        </ResponsiveCol>
      </Row>
    </div>
  );
}
